import { useCallback, useState } from 'react';
import Papa from 'papaparse';
import { z } from 'zod';
import type { Visitor } from '../../../types/form-data.types';
import type { VisitStepFieldsConfig } from '../../../hooks/use-ui-metadata-field-config.hook';
import {
  CSV_DELIMITERS_TO_GUESS,
  MAX_VISITORS_PER_UPLOAD,
  VISITOR_CSV_COLUMN_MAP,
  VISITOR_CSV_HEADERS,
} from '../constants';

interface UseUploadVisitorsCsvProps {
  onSuccess: (visitors: Visitor[]) => void;
  fieldsConfig: VisitStepFieldsConfig;
}

interface UseUploadVisitorsCsvReturn {
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  uploadedFileName: string | null;
  visitorCount: number | null;
  uploadError: string | null;
  validationError: string | null;
  isUploading: boolean;
  clearUpload: () => void;
}

const getField = (row: Record<string, string>, field: keyof typeof VISITOR_CSV_COLUMN_MAP): string =>
  row[VISITOR_CSV_COLUMN_MAP[field]] ?? '';

const isValidEmail = (email: string): boolean => z.email().safeParse(email).success;

/**
 * Validate if a CSV row has all required fields and valid data based on ui_metadata configuration
 */
// eslint-disable-next-line operator-linebreak
const createVisitorRowValidator =
  (fieldsConfig: VisitStepFieldsConfig) =>
  (row: Record<string, string>): boolean => {
    const firstName = getField(row, 'firstName').trim();
    const lastName = getField(row, 'lastName').trim();
    const company = getField(row, 'companyName').trim();
    const email = getField(row, 'email').trim();
    const phone = getField(row, 'phone').trim();

    // Always required fields
    if (!firstName || !lastName) {
      return false;
    }

    // Company name - check if required by config
    if (fieldsConfig.companyName.required && !company) {
      return false;
    }

    // Email - check if required by config and validate format if provided
    if (fieldsConfig.email.required && !email) {
      return false;
    }
    if (email && !isValidEmail(email)) {
      return false;
    }

    // Phone - check if required by config
    if (fieldsConfig.phone.required && !phone) {
      return false;
    }

    return true;
  };

/**
 * Convert CSV row to a Visitor object
 */
const csvRowToVisitor = (row: Record<string, string>): Visitor => ({
  firstName: getField(row, 'firstName').trim(),
  lastName: getField(row, 'lastName').trim(),
  companyName: getField(row, 'companyName').trim(),
  email: getField(row, 'email').trim() || undefined,
  phone: getField(row, 'phone').trim() || undefined,
});

/**
 * Generate a list of required field translation keys based on configuration
 */
const getRequiredFieldKeys = (fieldsConfig: VisitStepFieldsConfig): string[] => {
  const required: string[] = ['createVisitFormV2.visitStep.firstName', 'createVisitFormV2.visitStep.lastName'];

  if (fieldsConfig.companyName.required) {
    required.push('createVisitFormV2.visitStep.affiliation');
  }
  if (fieldsConfig.email.required) {
    required.push('createVisitFormV2.visitStep.email');
  }
  if (fieldsConfig.phone.required) {
    required.push('createVisitFormV2.visitStep.phone');
  }

  return required;
};

/**
 * Hook for uploading and parsing visitor CSV files with dynamic validation based on ui_metadata
 */
export const useUploadVisitorsCsv = ({
  onSuccess,
  fieldsConfig,
}: UseUploadVisitorsCsvProps): UseUploadVisitorsCsvReturn => {
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [visitorCount, setVisitorCount] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const clearUpload = useCallback(() => {
    setUploadedFileName(null);
    setVisitorCount(null);
    setUploadError(null);
    setValidationError(null);
  }, []);

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const file = event.target.files?.[0];

      // Reset state
      clearUpload();

      if (!file || !file.name?.toLowerCase()?.endsWith('.csv')) {
        setUploadError('errors.visitorCSVFileRequired');
        return;
      }

      setIsUploading(true);
      setUploadedFileName(file.name);

      // Create validator with current field configuration
      const isValidVisitorRow = createVisitorRowValidator(fieldsConfig);

      // Parse CSV file
      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        delimiter: '',
        delimitersToGuess: CSV_DELIMITERS_TO_GUESS,
        complete: (results) => {
          setIsUploading(false);

          // Check if headers match
          const headers = results.meta.fields || [];
          const hasValidHeaders = VISITOR_CSV_HEADERS.every((header) => headers.includes(header));
          if (!hasValidHeaders) {
            setUploadError('errors.invalidCsvHeaders');
            setUploadedFileName(null);
            return;
          }

          // Count total rows and validate
          const totalRows = results.data.length;

          // Check if total rows exceed maximum allowed
          if (totalRows > MAX_VISITORS_PER_UPLOAD) {
            setUploadError('errors.tooManyVisitors');
            setUploadedFileName(null);
            return;
          }
          const validVisitors = results.data.filter(isValidVisitorRow).map(csvRowToVisitor);

          if (validVisitors.length === 0) {
            setUploadError('errors.noValidVisitors');
            setUploadedFileName(null);
            return;
          }

          // Set total count (not just valid count)
          setVisitorCount(totalRows);

          // If some visitors are invalid, set validation error with required field keys
          if (validVisitors.length < totalRows) {
            const requiredFieldKeys = getRequiredFieldKeys(fieldsConfig);
            // Store as comma-separated translation keys - will be translated in the component
            setValidationError(requiredFieldKeys.join(','));
            // Clear form visitors to prevent submission with partial data
            onSuccess([]);
          } else {
            // All visitors are valid
            setValidationError(null);
            onSuccess(validVisitors);
          }
        },
        error: () => {
          setIsUploading(false);
          setUploadError('errors.csvParseError');
          setUploadedFileName(null);
        },
      });

      // Reset input value to allow re-uploading the same file
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
    },
    [onSuccess, clearUpload, fieldsConfig],
  );

  return {
    handleFileUpload,
    uploadedFileName,
    visitorCount,
    uploadError,
    validationError,
    isUploading,
    clearUpload,
  };
};
